import { useEffect, useRef, useState, type ReactNode } from "react";
import { useCustom, useCustomMutation, useGetLocale, useTranslate } from "@refinedev/core";
import { Button, StatusBadge } from "@fawkes/ui";
import { CheckCircle2, Globe, Image, PowerOff, Settings2, Sparkles, Trash2 } from "lucide-react";

import { getAccessToken } from "../auth";
import "./PensionOverzichten.css";
import { formatDateTime } from "../i18n/format";
import { foutBody } from "./klantverzoeken/types";
import { WizardRegels, WizardVersies, type Regel } from "./klantverzoeken/WizardRegelsBlok";
import { ApiClients } from "./klantverzoeken/ApiClientsBlok";
import "./Klantverzoeken.css";

/**
 * Portaalinstellingen (ontwerp hoofdstuk 15 en 26, mockup "Portaalinstellingen in backoffice"): het klantportaal
 * van dit pension aanzetten en inrichten. Thema zonder vrije CSS (bekende velden, contrastcontrole, logo als
 * PNG/JPG), modules, registratie, gezinsleden, reserveringen, berichten en eigen domeinen met DNS-verificatie.
 */

type T = (key: string, options?: { args?: unknown[] }) => string;
type Json = Record<string, unknown>;

const API_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? "/api";
const MODULES = ["dieren", "reserveringen", "dagopvang", "communicatie", "financieel", "documenten"] as const;
const VELDEN = ["telefoon", "noodcontact", "dierenarts", "geboortedatum", "dieren"] as const;
const PROFIELEN = ["VOLLEDIG", "VOLLEDIG_ZONDER_FINANCIEEL", "RESERVEREN", "MEEKIJKEN"] as const;
const CATEGORIEEN = ["ALGEMEEN", "RESERVERING", "FACTUUR"] as const;
type Tab = "algemeen" | "thema" | "modules" | "gezin" | "reserveren" | "berichten" | "financieel" | "api";
const TABS: Tab[] = ["algemeen", "thema", "modules", "gezin", "reserveren", "berichten", "financieel", "api"];

interface Host {
  hostnaam: string;
  geverifieerd: boolean;
  verificatieToken: string | null;
}

interface Tenant {
  id: string;
  code: string;
  naam: string;
  actief: boolean;
  thema: Json;
  themaEffectief: { primaireKleur: string; accentKleur: string; logoUrl: string | null };
  instellingen: Json;
  hosts: Host[];
}

interface Overzicht {
  tenants: Tenant[];
  vestigingen: { id: number; naam: string }[];
  verificatieRecord: string;
}

/** Een waarde diep in de instellingen lezen, bijvoorbeeld ["reserveringen", "vestigingId"]. */
function pad(j: Json, sleutels: string[]): unknown {
  return sleutels.reduce<unknown>((w, k) => (w && typeof w === "object" ? (w as Json)[k] : undefined), j);
}

/** Een kopie van de instellingen met één waarde gezet. */
function met(j: Json, sleutels: string[], waarde: unknown): Json {
  const [kop, ...rest] = sleutels;
  const huidig = (j[kop] && typeof j[kop] === "object" && !Array.isArray(j[kop]) ? (j[kop] as Json) : {});
  return { ...j, [kop]: rest.length === 0 ? waarde : met(huidig, rest, waarde) };
}

export function PortaalInstellingen() {
  const t = useTranslate() as T;
  const locale = useGetLocale()() ?? "nl";
  const { data, isLoading, refetch } = useCustom<Overzicht>({ url: "portaal-instellingen", method: "get" });
  const { mutateAsync, isLoading: bezig } = useCustomMutation();
  const [tab, setTab] = useState<Tab>("algemeen");
  const [melding, setMelding] = useState<{ tekst: string; fout: boolean } | null>(null);
  const overzicht = data?.data;
  const tenant = overzicht?.tenants[0];

  async function doe(url: string, method: "post" | "put" | "delete", values: object, gelukt?: string) {
    setMelding(null);
    try {
      await mutateAsync({ url, method, values, successNotification: false, errorNotification: false });
      await refetch();
      setMelding({ tekst: gelukt ?? t("portaalInstellingen.opgeslagen"), fout: false });
      return true;
    } catch (err) {
      setMelding({ tekst: foutBody(err).melding ?? t("portaalInstellingen.fout"), fout: true });
      return false;
    }
  }

  if (isLoading) {
    return <p className="kv__laden">{t("common.loading")}</p>;
  }

  return (
    <div className="kv pi">
      {/* De kop van Pensionoverzichten: rond icoon, grote titel, en rechts de stand van het portaal als tegels. */}
      <div className="po pi__kopvak">
        <header className="po-kop">
          <span className="po-kop__icoon" aria-hidden="true"><Settings2 size={26} /></span>
          <div className="po-kop__tekst">
            <h1>{t("portaalInstellingen.titel")}</h1>
            <p>{t("portaalInstellingen.subtitel")}</p>
          </div>
          {tenant && (
            <div className="da-overview__stats po-tellers" role="group">
              <button type="button" className="da-overview__stat" data-toon={tenant.actief ? "groen" : "rood"}
                onClick={() => { setTab("algemeen"); setMelding(null); }}>
                <span className="da-overview__stat-icon" aria-hidden="true">
                  {tenant.actief ? <CheckCircle2 size={20} /> : <PowerOff size={20} />}
                </span>
                <span className="da-overview__stat-body">
                  <span className="da-overview__stat-value pi__kopwaarde">{tenant.naam}</span>
                  <span className="da-overview__stat-label">{t(tenant.actief ? "portaalInstellingen.kop.actief" : "portaalInstellingen.kop.uit")}</span>
                </span>
              </button>
              <button type="button" className="da-overview__stat" onClick={() => { setTab("algemeen"); setMelding(null); }}>
                <span className="da-overview__stat-icon" aria-hidden="true"><Globe size={20} /></span>
                <span className="da-overview__stat-body">
                  <span className="da-overview__stat-value">
                    {tenant.hosts.filter((h) => h.geverifieerd).length}/{tenant.hosts.length}
                  </span>
                  <span className="da-overview__stat-label">{t("portaalInstellingen.kop.domeinen")}</span>
                </span>
              </button>
            </div>
          )}
        </header>
      </div>
      {!tenant ? (
        <Activeren t={t} bezig={bezig} onActiveer={(naam) => doe("portaal-instellingen/activeer", "post", { naam })} />
      ) : (
        <>
          <div className="po pi__tabvak">
            <div className="po-tabs" role="tablist">
              {TABS.map((x) => (
                <button key={x} type="button" role="tab" aria-selected={tab === x} data-actief={tab === x || undefined}
                  onClick={() => { setTab(x); setMelding(null); }}>
                  {t(`portaalInstellingen.tab.${x}`)}
                </button>
              ))}
            </div>
          </div>
          {melding && <p className={melding.fout ? "fawkes-error" : "pi__ok"} role={melding.fout ? "alert" : "status"}>{melding.tekst}</p>}
          {tab === "algemeen" && <Algemeen key={tenant.id} t={t} tenant={tenant} record={overzicht!.verificatieRecord} bezig={bezig} doe={doe} />}
          {tab === "thema" && <Thema key={tenant.id + JSON.stringify(tenant.thema)} t={t} tenant={tenant} bezig={bezig} doe={doe}
            onGeupload={() => { void refetch(); setMelding({ tekst: t("portaalInstellingen.opgeslagen"), fout: false }); }}
            onFout={(tekst) => setMelding({ tekst, fout: true })} melding={melding} />}
          {tab === "api" && <ApiClients t={t} tenantId={tenant.id} formatDatum={(d) => formatDateTime(d, locale)} />}
          {tab !== "algemeen" && tab !== "thema" && tab !== "api" && (
            <InstellingenForm key={tenant.id + tab + JSON.stringify(tenant.instellingen)} t={t} tab={tab} tenant={tenant}
              vestigingen={overzicht!.vestigingen} bezig={bezig}
              onBewaar={(instellingen) => doe(`portaal-instellingen/${tenant.id}/instellingen`, "put", instellingen)} />
          )}
        </>
      )}
    </div>
  );
}

type Doe = (url: string, method: "post" | "put" | "delete", values: object, gelukt?: string) => Promise<boolean>;

function Activeren({ t, bezig, onActiveer }: { t: T; bezig: boolean; onActiveer: (naam: string) => void }) {
  // Het portaal heet standaard zoals de organisatie: de naam van de vestiging waar je nu in werkt.
  const huidige = useCustom<{ naam?: string }>({ url: "vestigingen/current", method: "get", errorNotification: false })
    .data?.data?.naam ?? "";
  const [eigen, setNaam] = useState<string | null>(null);
  const naam = eigen ?? huidige;
  return (
    <section className="kvd__blok">
      <h2><Globe size={16} /> {t("portaalInstellingen.activeren.titel")}</h2>
      <p className="kvd__stil">{t("portaalInstellingen.activeren.uitleg")}</p>
      <form className="kvd__form" onSubmit={(e) => { e.preventDefault(); onActiveer(naam); }}>
        <label>{t("portaalInstellingen.naam")}<input required value={naam} onChange={(e) => setNaam(e.target.value)} /></label>
        <div className="kvd__acties"><Button variant="primary" type="submit" disabled={bezig}>{t("portaalInstellingen.activeren.knop")}</Button></div>
      </form>
    </section>
  );
}

function Algemeen({ t, tenant, record, bezig, doe }: { t: T; tenant: Tenant; record: string; bezig: boolean; doe: Doe }) {
  const [naam, setNaam] = useState(tenant.naam);
  const [actief, setActief] = useState(tenant.actief);
  const [host, setHost] = useState("");
  const basis = `portaal-instellingen/${tenant.id}`;
  return (
    <>
      <section className="kvd__blok">
        <h2>{t("portaalInstellingen.tab.algemeen")}</h2>
        <form className="kvd__form" onSubmit={(e) => { e.preventDefault(); void doe(`${basis}/algemeen`, "put", { naam, actief }); }}>
          <label>{t("portaalInstellingen.naam")}<input required maxLength={100} value={naam} onChange={(e) => setNaam(e.target.value)} /></label>
          <label className="kvd__keuze">
            <input type="checkbox" checked={actief} onChange={(e) => setActief(e.target.checked)} />
            {t("portaalInstellingen.actief")}
          </label>
          <p className="kvd__stil">{t("portaalInstellingen.actiefUitleg")}</p>
          <div className="kvd__acties"><Button variant="primary" type="submit" disabled={bezig}>{t("portaalInstellingen.opslaan")}</Button></div>
        </form>
      </section>
      <section className="kvd__blok">
        <h2><Globe size={16} /> {t("portaalInstellingen.domeinen.titel")}</h2>
        <p className="kvd__stil">{t("portaalInstellingen.domeinen.uitleg")}</p>
        {tenant.hosts.length === 0 && <p className="kvd__stil">{t("portaalInstellingen.domeinen.geen")}</p>}
        <ul className="kpt__lijst">
          {tenant.hosts.map((h) => (
            <li key={h.hostnaam}>
              <span className="kpt__wie">
                <strong>{h.hostnaam}</strong>
                {!h.geverifieerd && h.verificatieToken && (
                  <span className="kvd__stil">
                    {t("portaalInstellingen.domeinen.txt", { args: [`${record}.${h.hostnaam}`, h.verificatieToken] })}
                  </span>
                )}
              </span>
              {h.geverifieerd
                ? <StatusBadge tone="success"><CheckCircle2 size={13} aria-hidden="true" /> {t("portaalInstellingen.domeinen.geverifieerd")}</StatusBadge>
                : <StatusBadge tone="warning">{t("portaalInstellingen.domeinen.nietGeverifieerd")}</StatusBadge>}
              <span className="kpt__knoppen">
                {!h.geverifieerd && (
                  <button type="button" className="kpt__link" disabled={bezig}
                    onClick={() => void doe(`${basis}/hosts/${encodeURIComponent(h.hostnaam)}/verifieer`, "post", {}, t("portaalInstellingen.domeinen.gelukt"))}>
                    {t("portaalInstellingen.domeinen.verifieer")}
                  </button>
                )}
                <button type="button" className="kpt__link kpt__link--gevaar" disabled={bezig} aria-label={t("portaalInstellingen.domeinen.verwijder")}
                  onClick={() => void doe(`${basis}/hosts/${encodeURIComponent(h.hostnaam)}`, "delete", {})}>
                  <Trash2 size={14} aria-hidden="true" />
                </button>
              </span>
            </li>
          ))}
        </ul>
        <form className="pi__rij" onSubmit={async (e) => { e.preventDefault(); if (await doe(`${basis}/hosts`, "post", { hostnaam: host })) setHost(""); }}>
          <input aria-label={t("portaalInstellingen.domeinen.nieuw")} placeholder="mijn.pensionnaam.nl" value={host} onChange={(e) => setHost(e.target.value)} />
          <Button variant="secondary" type="submit" disabled={bezig || !host.trim()}>{t("portaalInstellingen.domeinen.toevoegen")}</Button>
        </form>
      </section>
    </>
  );
}

function Thema({ t, tenant, bezig, doe, onGeupload, onFout, melding }: {
  t: T; tenant: Tenant; bezig: boolean; doe: Doe; onGeupload: () => void; onFout: (tekst: string) => void;
  melding: { tekst: string; fout: boolean } | null;
}) {
  const [thema, setThema] = useState<Json>(tenant.thema ?? {});
  const bestand = useRef<HTMLInputElement>(null);
  const [soort, setSoort] = useState<"LOGO" | "HEADER">("LOGO");
  const tekst = (k: string) => (typeof thema[k] === "string" ? (thema[k] as string) : "");
  const contact = (thema.contact as Json | undefined) ?? {};
  const zet = (k: string, w: string) => setThema({ ...thema, [k]: w });
  // Wat het portaal echt toont: een te lichte hoofdkleur maakt het (net als de server) donkerder tot witte tekst leesbaar is.
  const gekozen = /^#[0-9a-fA-F]{6}$/.test(tekst("primaireKleur")) ? tekst("primaireKleur").toUpperCase() : "";
  const effectief = gekozen ? leesbaarOpWit(gekozen) : "";
  const aangepast = !!gekozen && effectief !== gekozen;
  // Kleuren uit de website van het pension, net als bij de huisstijl van nazorg: een adres in, een palet uit.
  const [website, setWebsite] = useState("");
  const [siteBezig, setSiteBezig] = useState(false);
  const [siteMelding, setSiteMelding] = useState<{ tekst: string; fout: boolean } | null>(null);
  const [gevonden, setGevonden] = useState<string[]>([]);
  const kiesHoofdkleur = (hex: string) => setThema((oud) => ({ ...oud, primaireKleur: hex.toUpperCase() }));
  const [bron, setBron] = useState<"website" | "logo">("website");
  function neemPaletOver(lijst: string[], waar: "website" | "logo") {
    const kleuren = lijst.filter((k) => /^#[0-9a-fA-F]{6}$/.test(k)).map((k) => k.toUpperCase()).slice(0, 8);
    setGevonden(kleuren);
    setBron(waar);
    const p = kiesPalet(kleuren);
    if (p) setThema((oud) => ({ ...oud, primaireKleur: p.primair, accentKleur: p.accent }));
    setSiteMelding({ tekst: t(waar === "logo" ? "portaalInstellingen.site.klaarLogo" : "portaalInstellingen.site.klaar"), fout: !p });
  }
  async function kleurenUitLogo() {
    setSiteBezig(true);
    setSiteMelding(null);
    try {
      const res = await vraagAi({ url: `portaal-instellingen/${tenant.id}/kleuren-uit-logo`, method: "post", values: {},
        successNotification: false, errorNotification: false });
      neemPaletOver((res.data as unknown as { gevonden?: string[] }).gevonden ?? [], "logo");
    } catch (err) {
      setSiteMelding({ tekst: foutBody(err).melding ?? t("portaalInstellingen.site.fout"), fout: true });
    } finally {
      setSiteBezig(false);
    }
  }
  async function kleurenUitWebsite() {
    if (!website.trim()) return;
    setSiteBezig(true);
    setSiteMelding(null);
    try {
      const adres = /^https?:\/\//i.test(website.trim()) ? website.trim() : `https://${website.trim()}`;
      const res = await vraagAi({ url: "thema/uit-website", method: "post", values: { url: adres },
        successNotification: false, errorNotification: false });
      const palet = res.data as unknown as { primair?: string; gevonden?: string[] };
      neemPaletOver(palet.gevonden?.length ? palet.gevonden : palet.primair ? [palet.primair] : [], "website");
    } catch (err) {
      setSiteMelding({ tekst: foutBody(err).melding ?? t("portaalInstellingen.site.fout"), fout: true });
    } finally {
      setSiteBezig(false);
    }
  }
  // Teksten laten voorstellen door de AI, net als bij nazorg en communicatie; opslaan blijft een eigen stap.
  const { mutateAsync: vraagAi } = useCustomMutation();
  const [wens, setWens] = useState("");
  const [aiBezig, setAiBezig] = useState<string | null>(null);
  const [aiFout, setAiFout] = useState<string | null>(null);
  const [aiKlaar, setAiKlaar] = useState(false);
  async function schrijf(velden: string[], sleutel: string) {
    setAiBezig(sleutel);
    setAiFout(null);
    setAiKlaar(false);
    try {
      const res = await vraagAi({ url: `portaal-instellingen/${tenant.id}/ai/teksten`, method: "post",
        values: { velden, wens }, successNotification: false, errorNotification: false });
      const v = res.data as unknown as Record<string, string | null>;
      setThema((oud) => {
        const nieuw = { ...oud };
        for (const k of velden) if (typeof v[k] === "string" && v[k]) nieuw[k] = v[k] as string;
        return nieuw;
      });
      setAiKlaar(true);
    } catch (err) {
      setAiFout(foutBody(err).melding ?? t("portaalInstellingen.ai.fout"));
    } finally {
      setAiBezig(null);
    }
  }

  async function upload(f: File) {
    const body = new FormData();
    body.append("bestand", f);
    const token = getAccessToken();
    const res = await fetch(`${API_URL}/portaal-instellingen/${tenant.id}/afbeelding?soort=${soort}`, {
      method: "POST", headers: token ? { Authorization: `Bearer ${token}` } : undefined, body,
    });
    if (res.ok) {
      onGeupload();
    } else {
      const b = (await res.json().catch(() => ({}))) as { melding?: string };
      onFout(b.melding ?? t("portaalInstellingen.fout"));
    }
    if (bestand.current) bestand.current.value = "";
  }

  return (
    <section className="kvd__blok">
      <h2>{t("portaalInstellingen.tab.thema")}</h2>
      <form className="kvd__form" onSubmit={(e) => { e.preventDefault(); void doe(`portaal-instellingen/${tenant.id}/thema`, "put", thema); }}>
        <div className="pi__rij">
          {tenant.themaEffectief.logoUrl && <img className="pi__logo" src={tenant.themaEffectief.logoUrl} alt={t("portaalInstellingen.thema.logo")} />}
          <select aria-label={t("portaalInstellingen.thema.welke")} value={soort} onChange={(e) => setSoort(e.target.value as "LOGO" | "HEADER")}>
            <option value="LOGO">{t("portaalInstellingen.thema.logo")}</option>
            <option value="HEADER">{t("portaalInstellingen.thema.header")}</option>
          </select>
          <input ref={bestand} type="file" accept="image/png,image/jpeg" hidden onChange={(e) => { const f = e.target.files?.[0]; if (f) void upload(f); }} />
          <Button variant="secondary" type="button" onClick={() => bestand.current?.click()}>
            <Image size={16} aria-hidden="true" /> {t("portaalInstellingen.thema.upload")}
          </Button>
        </div>
        <p className="kvd__stil">{t("portaalInstellingen.thema.uploadUitleg")}</p>
        <div className="pi__ai">
          <div className="pi__ai-kop">
            <Globe size={18} aria-hidden="true" />
            <div>
              <strong>{t("portaalInstellingen.site.titel")}</strong>
              <p className="kvd__stil">{t("portaalInstellingen.site.uitleg")}</p>
            </div>
          </div>
          <div className="pi__rij">
            <input className="pi__ai-wens" type="text" inputMode="url" value={website} placeholder="www.jouwpension.nl"
              aria-label={t("portaalInstellingen.site.adres")} onChange={(e) => setWebsite(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); void kleurenUitWebsite(); } }} />
            <Button variant="secondary" type="button" disabled={siteBezig || !website.trim()} onClick={() => void kleurenUitWebsite()}>
              <Sparkles size={16} aria-hidden="true" /> {siteBezig ? t("portaalInstellingen.site.bezig") : t("portaalInstellingen.site.knop")}
            </Button>
            {tenant.themaEffectief.logoUrl && (
              <Button variant="secondary" type="button" disabled={siteBezig} onClick={() => void kleurenUitLogo()}>
                <Image size={16} aria-hidden="true" /> {t("portaalInstellingen.site.logo")}
              </Button>
            )}
          </div>
          {gevonden.length > 0 && (
            <div className="pi__kleuren" role="group" aria-label={t("portaalInstellingen.site.gevonden")}>
              <span className="kvd__stil">{t(bron === "logo" ? "portaalInstellingen.site.gevondenLogo" : "portaalInstellingen.site.gevonden")}</span>
              {gevonden.map((k) => (
                <button key={k} type="button" className="pi__kleur" style={{ background: k }} title={k}
                  aria-pressed={tekst("primaireKleur").toUpperCase() === k.toUpperCase()}
                  aria-label={t("portaalInstellingen.site.kies", { args: [k] })} onClick={() => kiesHoofdkleur(k)} />
              ))}
            </div>
          )}
          {siteMelding && <p className={siteMelding.fout ? "fawkes-error" : "pi__ok"} role={siteMelding.fout ? "alert" : "status"}>{siteMelding.tekst}</p>}
        </div>
        <div className="pi__raster">
          {(["primaireKleur", "accentKleur"] as const).map((k) => (
            <label key={k}>
              {t(`portaalInstellingen.thema.${k}`)}
              <span className="pi__rij">
                <input type="color" value={tekst(k) || (k === "primaireKleur" ? "#2F765F" : "#E8F2EE")} onChange={(e) => zet(k, e.target.value.toUpperCase())} />
                <input value={tekst(k)} placeholder={k === "primaireKleur" ? "#2F765F" : "#E8F2EE"} onChange={(e) => zet(k, e.target.value)} />
              </span>
            </label>
          ))}
          <label>
            {t("portaalInstellingen.thema.lettertype")}
            <select value={tekst("lettertype") || "Inter"} onChange={(e) => zet("lettertype", e.target.value)}>
              {["Inter", "Nunito", "Source Sans 3", "Lato"].map((f) => <option key={f}>{f}</option>)}
            </select>
          </label>
          <label>
            {t("portaalInstellingen.thema.hoekstijl")}
            <select value={tekst("hoekstijl") || "zacht"} onChange={(e) => zet("hoekstijl", e.target.value)}>
              {["compact", "zacht", "rond"].map((h) => <option key={h} value={h}>{t(`portaalInstellingen.thema.hoek.${h}`)}</option>)}
            </select>
          </label>
        </div>
        {aangepast && (
          <p className="kvd__stil pi__contrast">
            <span className="pi__kleur pi__kleur--klein" style={{ background: effectief }} aria-hidden="true" />
            {t("portaalInstellingen.thema.donkerder", { args: [gekozen, effectief] })}
          </p>
        )}
        <div className="pi__ai">
          <div className="pi__ai-kop">
            <Sparkles size={18} aria-hidden="true" />
            <div>
              <strong>{t("portaalInstellingen.ai.titel")}</strong>
              <p className="kvd__stil">{t("portaalInstellingen.ai.uitleg")}</p>
            </div>
          </div>
          <div className="pi__rij">
            <input className="pi__ai-wens" value={wens} maxLength={500} placeholder={t("portaalInstellingen.ai.wensPlaats")}
              aria-label={t("portaalInstellingen.ai.wens")} onChange={(e) => setWens(e.target.value)} />
            <Button variant="secondary" type="button" disabled={aiBezig !== null}
              onClick={() => void schrijf(["welkomsttekst", "hulptekst", "footertekst"], "alles")}>
              <Sparkles size={16} aria-hidden="true" /> {aiBezig === "alles" ? t("portaalInstellingen.ai.bezig") : t("portaalInstellingen.ai.alles")}
            </Button>
          </div>
          {aiFout && <p className="fawkes-error" role="alert">{aiFout}</p>}
          {aiKlaar && <p className="pi__ok" role="status">{t("portaalInstellingen.ai.klaar")}</p>}
        </div>
        {(["welkomsttekst", "hulptekst", "footertekst"] as const).map((k) => (
          <label key={k}>
            <span className="pi__label-ai">
              {t(`portaalInstellingen.thema.${k}`)}
              <button type="button" className="pi__ai-knop" disabled={aiBezig !== null} onClick={() => void schrijf([k], k)}
                title={t("portaalInstellingen.ai.een")}>
                <Sparkles size={13} aria-hidden="true" /> {aiBezig === k ? t("portaalInstellingen.ai.bezig") : t("portaalInstellingen.ai.een")}
              </button>
            </span>
            <textarea rows={3} maxLength={2000} value={tekst(k)} onChange={(e) => zet(k, e.target.value)} />
          </label>
        ))}
        <div className="pi__raster">
          {(["telefoon", "email", "openingstijden"] as const).map((k) => (
            <label key={k}>{t(`portaalInstellingen.thema.contact.${k}`)}
              <input maxLength={200} value={typeof contact[k] === "string" ? (contact[k] as string) : ""}
                onChange={(e) => setThema({ ...thema, contact: { ...contact, [k]: e.target.value } })} />
            </label>
          ))}
        </div>
        <Voorbeeld thema={thema} tenant={tenant} t={t} />
        <div className="kvd__acties">
          <Button variant="primary" type="submit" disabled={bezig}>{t("portaalInstellingen.opslaan")}</Button>
          {/* De algemene melding staat bovenaan de pagina; hier onderaan zie je meteen of het lukte. */}
          {melding && <span className={melding.fout ? "fawkes-error" : "pi__ok"} role={melding.fout ? "alert" : "status"}>{melding.tekst}</span>}
                </div>
      </form>
    </section>
  );
}

/** De lichte tint van een kleur (12% kleur op wit): in het portaal is de accentkleur de zachte achtergrond. */
export function lichteTint(hex: string): string {
  const m = /^#?([0-9a-fA-F]{6})$/.exec(hex.trim());
  if (!m) return "#E8F2EE";
  const n = parseInt(m[1], 16);
  const meng = (c: number) => Math.round(255 - (255 - c) * 0.12);
  const [r, g, b] = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map(meng);
  return `#${[r, g, b].map((c) => c.toString(16).padStart(2, "0")).join("")}`.toUpperCase();
}

/** Zoals PortaalThemaService.leesbaarOpWit: stapsgewijs donkerder tot witte tekst een contrast van 4,5:1 heeft. */
export function leesbaarOpWit(hex: string): string {
  let [r, g, b] = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));
  for (let i = 0; i < 40 && contrastMetWit(r, g, b) < 4.5; i++) {
    [r, g, b] = [r, g, b].map((c) => Math.trunc(c * 0.92));
  }
  return `#${[r, g, b].map((c) => c.toString(16).padStart(2, "0")).join("")}`.toUpperCase();
}

export function contrastMetWit(r: number, g: number, b: number): number {
  const lin = (c: number) => { const v = c / 255; return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4; };
  return 1.05 / (0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b) + 0.05);
}

/** Hoe kleurrijk een kleur is (max − min van de kanalen, 0–1): bijna-zwart, grijs en wit tellen niet als huisstijlkleur. */
function verzadiging(hex: string): number {
  const [r, g, b] = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16) / 255);
  return Math.max(r, g, b) - Math.min(r, g, b);
}

/**
 * Van gevonden kleuren (vaakst eerst) naar een palet voor het portaal. De hoofdkleur draagt witte tekst (knoppen,
 * kop), dus: de eerste échte kleur waarop wit leesbaar is; anders de eerste donkere (zoals zwart); anders de meest
 * voorkomende, donkerder gemaakt. De accentkleur is de zachte achtergrond: een lichte tint van de meest voorkomende
 * kleur, zodat het portaal naar de huisstijl ruikt zonder dat tekst onleesbaar wordt.
 */
export function kiesPalet(gevonden: string[]): { primair: string; accent: string } | null {
  const kleuren = gevonden.filter((k) => /^#[0-9A-Fa-f]{6}$/.test(k)).map((k) => k.toUpperCase());
  const rgb = (k: string) => [1, 3, 5].map((i) => parseInt(k.slice(i, i + 2), 16)) as [number, number, number];
  const bijnaWit = (k: string) => contrastMetWit(...rgb(k)) < 1.25;
  const echt = kleuren.filter((k) => !bijnaWit(k));
  if (echt.length === 0) return null;
  const leesbaar = (k: string) => contrastMetWit(...rgb(k)) >= 4.5;
  const primair = echt.find((k) => leesbaar(k) && verzadiging(k) > 0.25) ?? echt.find(leesbaar) ?? leesbaarOpWit(echt[0]);
  const dominant = echt.find((k) => verzadiging(k) > 0.25) ?? echt[0];
  return { primair, accent: lichteTint(dominant) };
}

function Voorbeeld({ thema, tenant, t }: { thema: Json; tenant: Tenant; t: T }) {
  const gekozen = typeof thema.primaireKleur === "string" && /^#[0-9a-fA-F]{6}$/.test(thema.primaireKleur) ? thema.primaireKleur : null;
  const kleur = gekozen ? leesbaarOpWit(gekozen.toUpperCase()) : tenant.themaEffectief.primaireKleur;
  const accent = typeof thema.accentKleur === "string" && /^#[0-9a-fA-F]{6}$/.test(thema.accentKleur) ? thema.accentKleur : tenant.themaEffectief.accentKleur;
  const hoek = thema.hoekstijl === "compact" ? 4 : thema.hoekstijl === "rond" ? 20 : 12;
  return (
    <div className="pi__voorbeeld" aria-label={t("portaalInstellingen.thema.voorbeeld")}
      style={{ fontFamily: `${String(thema.lettertype ?? "Inter")}, system-ui, sans-serif`, background: accent, borderRadius: hoek }}>
      <strong>{tenant.naam}</strong>
      <p>{typeof thema.welkomsttekst === "string" && thema.welkomsttekst ? thema.welkomsttekst : t("portaalInstellingen.thema.voorbeeldTekst")}</p>
      <span className="pi__knop" style={{ background: kleur, borderRadius: hoek }}>{t("portaalInstellingen.thema.voorbeeldKnop")}</span>
    </div>
  );
}

function InstellingenForm({ t, tab, tenant, vestigingen, bezig, onBewaar }: {
  t: T; tab: Tab; tenant: Tenant; vestigingen: { id: number; naam: string }[]; bezig: boolean; onBewaar: (i: Json) => void;
}) {
  const locale = useGetLocale()() ?? "nl";
  const [i, setI] = useState<Json>(tenant.instellingen ?? {});
  useEffect(() => setI(tenant.instellingen ?? {}), [tenant.instellingen]);
  const zet = (sleutels: string[], w: unknown) => setI((oud) => met(oud, sleutels, w));
  const lees = (sleutels: string[]) => pad(i, sleutels);
  const tekst = (sleutels: string[]) => (typeof lees(sleutels) === "string" ? (lees(sleutels) as string) : "");
  const getal = (sleutels: string[], standaard: number) => (typeof lees(sleutels) === "number" ? (lees(sleutels) as number) : standaard);
  const vink = (sleutels: string[], standaard: boolean) => (typeof lees(sleutels) === "boolean" ? (lees(sleutels) as boolean) : standaard);
  const Vink = ({ s, standaard, label }: { s: string[]; standaard: boolean; label: string }) => (
    <label className="kvd__keuze"><input type="checkbox" checked={vink(s, standaard)} onChange={(e) => zet(s, e.target.checked)} />{label}</label>
  );
  const Getal = ({ s, standaard, label, min, max }: { s: string[]; standaard: number; label: string; min: number; max: number }) => (
    <label>{label}<input type="number" min={min} max={max} value={getal(s, standaard)} onChange={(e) => zet(s, Number(e.target.value))} /></label>
  );
  const Tekst = ({ s, label, placeholder }: { s: string[]; label: string; placeholder?: string }) => (
    <label>{label}<input value={tekst(s)} placeholder={placeholder} onChange={(e) => zet(s, e.target.value || undefined)} /></label>
  );

  let inhoud: ReactNode = null;
  if (tab === "modules") {
    inhoud = (
      <>
        <h3>{t("portaalInstellingen.modules.titel")}</h3>
        {MODULES.map((m) => <span key={m}>{Vink({ s: ["modules", m], standaard: true, label: t(`portaalInstellingen.module.${m}`) })}</span>)}
        <h3>{t("portaalInstellingen.inloggen.titel")}</h3>
        {Vink({ s: ["inloggen", "bestaandeKlanten"], standaard: true, label: t("portaalInstellingen.inloggen.bestaandeKlanten") })}
        <p className="kvd__stil">{t("portaalInstellingen.inloggen.uitleg")}</p>
        <h3>{t("portaalInstellingen.registratie.titel")}</h3>
        <label>{t("portaalInstellingen.registratie.zelf")}
          <select value={tekst(["registratie"]) || "ALLEEN_UITNODIGING"} onChange={(e) => zet(["registratie"], e.target.value)}>
            {["AAN", "ALLEEN_UITNODIGING", "UIT"].map((r) => <option key={r} value={r}>{t(`portaalInstellingen.registratie.${r}`)}</option>)}
          </select>
        </label>
        <div className="pi__raster">
          {VELDEN.map((v) => (
            <label key={v}>{t(`portaalInstellingen.veld.${v}`)}
              <select value={tekst(["registratieFormulier", "velden", v]) || ""} onChange={(e) => zet(["registratieFormulier", "velden", v], e.target.value || undefined)}>
                <option value="">{t("portaalInstellingen.standaard")}</option>
                {["VERPLICHT", "OPTIONEEL", "VERBORGEN"].map((x) => <option key={x} value={x}>{t(`portaalInstellingen.veldstand.${x}`)}</option>)}
              </select>
            </label>
          ))}
        </div>
        <div className="pi__raster">
          {Tekst({ s: ["registratieFormulier", "privacyUrl"], label: t("portaalInstellingen.registratie.privacyUrl"), placeholder: "https://" })}
          {Tekst({ s: ["registratieFormulier", "privacyVersie"], label: t("portaalInstellingen.registratie.privacyVersie") })}
          {Tekst({ s: ["registratieFormulier", "voorwaardenUrl"], label: t("portaalInstellingen.registratie.voorwaardenUrl"), placeholder: "https://" })}
          {Tekst({ s: ["registratieFormulier", "voorwaardenVersie"], label: t("portaalInstellingen.registratie.voorwaardenVersie") })}
        </div>
        {Tekst({ s: ["registratieFormulier", "doorlooptijd"], label: t("portaalInstellingen.registratie.doorlooptijd") })}
        {Vink({ s: ["registratieFormulier", "reserverenVoorGoedkeuring"], standaard: true, label: t("portaalInstellingen.registratie.reserverenVoorGoedkeuring") })}
        <h3>{t("portaalInstellingen.dieren.titel")}</h3>
        {Vink({ s: ["dieren", "toonBijzonderheden"], standaard: false, label: t("portaalInstellingen.dieren.bijzonderheden") })}
      </>
    );
  } else if (tab === "gezin") {
    const profielen = Array.isArray(lees(["gezinsleden", "profielen"])) ? (lees(["gezinsleden", "profielen"]) as string[]) : [];
    inhoud = (
      <>
        {Getal({ s: ["gezinsleden", "maximum"], standaard: 5, min: 1, max: 20, label: t("portaalInstellingen.gezin.maximum") })}
        {Vink({ s: ["gezinsleden", "toegangNaGoedkeuring"], standaard: false, label: t("portaalInstellingen.gezin.naGoedkeuring") })}
        <label>{t("portaalInstellingen.gezin.standaardProfiel")}
          <select value={tekst(["gezinsleden", "standaardProfiel"]) || "VOLLEDIG_ZONDER_FINANCIEEL"} onChange={(e) => zet(["gezinsleden", "standaardProfiel"], e.target.value)}>
            {PROFIELEN.map((p) => <option key={p} value={p}>{t(`portaalInstellingen.profiel.${p}`)}</option>)}
          </select>
        </label>
        <fieldset className="kvd__dieren">
          <legend>{t("portaalInstellingen.gezin.profielen")}</legend>
          {PROFIELEN.map((p) => (
            <label key={p} className="kvd__keuze">
              <input type="checkbox" checked={profielen.length === 0 || profielen.includes(p)}
                onChange={(e) => {
                  const huidig = profielen.length === 0 ? [...PROFIELEN] : profielen;
                  zet(["gezinsleden", "profielen"], e.target.checked ? [...huidig, p] : huidig.filter((x) => x !== p));
                }} />
              {t(`portaalInstellingen.profiel.${p}`)}
            </label>
          ))}
        </fieldset>
      </>
    );
  } else if (tab === "reserveren") {
    inhoud = (
      <>
        <label>{t("portaalInstellingen.reserveren.vestiging")}
          <select value={String(lees(["reserveringen", "vestigingId"]) ?? "")} onChange={(e) => zet(["reserveringen", "vestigingId"], e.target.value ? Number(e.target.value) : undefined)}>
            <option value="">{t("portaalInstellingen.reserveren.uit")}</option>
            {vestigingen.map((v) => <option key={v.id} value={v.id}>{v.naam}</option>)}
          </select>
        </label>
        <p className="kvd__stil">{t("portaalInstellingen.reserveren.wizardUitleg")}</p>
        <label>{t("portaalInstellingen.reserveren.beschikbaarheid")}
          <select value={tekst(["reserveringen", "beschikbaarheid"]) || "INDICATIEF"} onChange={(e) => zet(["reserveringen", "beschikbaarheid"], e.target.value)}>
            {["NIET_TONEN", "INDICATIEF", "REALTIME"].map((b) => <option key={b} value={b}>{t(`portaalInstellingen.beschikbaarheid.${b}`)}</option>)}
          </select>
        </label>
        <div className="pi__raster">
          {Getal({ s: ["reserveringen", "prijsDrempel", "bedrag"], standaard: 0, min: 0, max: 10000, label: t("portaalInstellingen.reserveren.drempelBedrag") })}
          {Getal({ s: ["reserveringen", "prijsDrempel", "procent"], standaard: 0, min: 0, max: 100, label: t("portaalInstellingen.reserveren.drempelProcent") })}
          {Getal({ s: ["reserveringen", "dagenVooruit"], standaard: 1, min: 0, max: 60, label: t("portaalInstellingen.reserveren.dagenVooruit") })}
          {Getal({ s: ["reserveringen", "conceptDagen"], standaard: 30, min: 1, max: 365, label: t("portaalInstellingen.reserveren.conceptDagen") })}
          {Tekst({ s: ["reserveringen", "voorwaardenUrl"], label: t("portaalInstellingen.reserveren.voorwaardenUrl"), placeholder: "https://" })}
          {Tekst({ s: ["reserveringen", "voorwaardenVersie"], label: t("portaalInstellingen.reserveren.voorwaardenVersie") })}
        </div>
        <p className="kvd__stil">{t("portaalInstellingen.reserveren.drempelUitleg")}</p>
        <h3>{t("portaalInstellingen.regels.titel")}</h3>
        <p className="kvd__stil">{t("portaalInstellingen.regels.uitleg")}</p>
        <WizardRegels t={t} vestigingId={typeof lees(["reserveringen", "vestigingId"]) === "number" ? (lees(["reserveringen", "vestigingId"]) as number) : null}
          regels={Array.isArray(lees(["reserveringen", "regels"])) ? (lees(["reserveringen", "regels"]) as Regel[]) : []}
          onChange={(r) => zet(["reserveringen", "regels"], r.length ? r : undefined)} />
      </>
    );
  } else if (tab === "berichten") {
    const categorieen = Array.isArray(lees(["communicatie", "categorieen"])) ? (lees(["communicatie", "categorieen"]) as string[]) : ["ALGEMEEN", "RESERVERING"];
    inhoud = (
      <>
        {Vink({ s: ["communicatie", "berichten"], standaard: false, label: t("portaalInstellingen.berichten.aan") })}
        <p className="kvd__stil">{t("portaalInstellingen.berichten.uitleg")}</p>
        <fieldset className="kvd__dieren">
          <legend>{t("portaalInstellingen.berichten.categorieen")}</legend>
          {CATEGORIEEN.map((c) => (
            <label key={c} className="kvd__keuze">
              <input type="checkbox" checked={categorieen.includes(c)}
                onChange={(e) => zet(["communicatie", "categorieen"], e.target.checked ? [...categorieen, c] : categorieen.filter((x) => x !== c))} />
              {t(`klantberichten.categorie.${c}`)}
            </label>
          ))}
        </fieldset>
        {Getal({ s: ["communicatie", "maxPerDag"], standaard: 20, min: 1, max: 100, label: t("portaalInstellingen.berichten.maxPerDag") })}
        {Vink({ s: ["communicatie", "gedeeld"], standaard: true, label: t("portaalInstellingen.berichten.gedeeld") })}
      </>
    );
  } else if (tab === "financieel") {
    inhoud = (
      <>
        {Vink({ s: ["financieel", "onlineBetalen"], standaard: false, label: t("portaalInstellingen.financieel.onlineBetalen") })}
        <label>{t("portaalInstellingen.financieel.provider")}
          <select value={tekst(["financieel", "provider"]) || "MOLLIE"} onChange={(e) => zet(["financieel", "provider"], e.target.value)}>
            <option value="MOLLIE">Mollie</option>
            <option value="TEST">{t("portaalInstellingen.financieel.test")}</option>
          </select>
        </label>
        <p className="kvd__stil">{t("portaalInstellingen.financieel.uitleg")}</p>
        <h3>{t("portaalInstellingen.documenten.titel")}</h3>
        {Vink({ s: ["documenten", "uploaden"], standaard: true, label: t("portaalInstellingen.documenten.uploaden") })}
        {Vink({ s: ["documenten", "dierDocumentenTonen"], standaard: false, label: t("portaalInstellingen.documenten.tonen") })}
        <h3>{t("portaalInstellingen.privacy.titel")}</h3>
        {Vink({ s: ["privacy", "supportZichtbaar"], standaard: true, label: t("portaalInstellingen.privacy.supportZichtbaar") })}
      </>
    );
  }

  return (
    <section className="kvd__blok">
      <h2>{t(`portaalInstellingen.tab.${tab}`)}</h2>
      <form className="kvd__form" onSubmit={(e) => { e.preventDefault(); onBewaar(i); }}>
        {inhoud}
        <div className="kvd__acties"><Button variant="primary" type="submit" disabled={bezig}>{t("portaalInstellingen.opslaan")}</Button></div>
      </form>
      {tab === "reserveren" && (
        <WizardVersies t={t} formatDatum={(d) => formatDateTime(d, locale)}
          vestigingId={typeof pad(tenant.instellingen ?? {}, ["reserveringen", "vestigingId"]) === "number"
            ? (pad(tenant.instellingen ?? {}, ["reserveringen", "vestigingId"]) as number) : null} />
      )}
    </section>
  );
}
